import { createFileRoute } from '@tanstack/react-router';
import { useOrganization } from '@/hooks/useOrganization';
import { useIntegrations, type Integration } from '@/hooks/useIntegrations';
import { Loader } from '@/components/ui/loader';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { HomeIcon, BuildingIcon, MessageSquareIcon, ExternalLinkIcon } from 'lucide-react';
import { IntegrationCard } from '@/components/integrations/IntegrationCard';

export const Route = createFileRoute('/_authenticated/org/$orgId/integrations')({
  // Note: We can't preload data here because Clerk token is not available yet
  // Data will be loaded in the component once Clerk is ready
  component: OrganizationIntegrationsPage,
});

function OrganizationIntegrationsPage() {
  const { orgId } = Route.useParams();
  const { organization, isLoading } = useOrganization(orgId);

  const { data: integrationsData, isLoading: integrationsLoading } = useIntegrations(orgId);
  const integrationsResponse = integrationsData as { integrations?: Integration[] } | undefined;
  const dbIntegrations: Integration[] = integrationsResponse?.integrations || [];

  // Define available integrations
  const availableIntegrations = [
    { id: 'github', name: 'GitHub', description: 'Receive webhooks from GitHub repositories', type: 'github' as const },
    { id: 'vercel', name: 'Vercel', description: 'Receive webhooks from Vercel deployments', type: 'webhook' as const },
    { id: 'slack', name: 'Slack', description: 'Receive webhooks from Slack workspaces', type: 'slack' as const },
  ];

  // Map database integrations to available integrations
  const integrations = availableIntegrations.map(available => {
    const dbIntegration = dbIntegrations.find((i: Integration) => i.type === available.type);
    return {
      ...available,
      connected: dbIntegration?.status === 'active',
      integration: dbIntegration,
    };
  });

  if (isLoading || integrationsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader variant="spinner" size="lg" color="accent" text="Loading integrations..." />
      </div>
    );
  }

  const breadcrumbs = [
    {
      name: 'Dashboard',
      to: '/dashboard',
      icon: HomeIcon,
    },
    {
      name: organization?.name || 'Organization',
      to: `/org/${organization?.slug || orgId}`,
      icon: BuildingIcon,
    },
    {
      name: 'Integrations',
      to: `/org/${organization?.slug || orgId}/integrations`,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={breadcrumbs}
        title="Incoming Webhooks"
        subtitle={organization ? `Configure integrations to receive webhooks from external services in ${organization.name}` : undefined}
      />

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">How Incoming Webhooks Work</h3>
        <p className="text-sm text-blue-800">
          <strong>Incoming Webhooks</strong> allow external services (like GitHub, Vercel, Slack) to send events to your organization.
          When you connect an integration, you'll receive a webhook URL that you need to configure in the external service.
          The external service will then send webhooks to that URL when events occur.
        </p>
        <p className="text-sm text-blue-700 mt-2">
          <strong>Note:</strong> This is different from <strong>Outgoing Webhooks</strong> (in the Webhooks section), where you send events to external URLs.
        </p>
      </div>

      <div className="space-y-3">
        {integrations.map((integration) => (
          <IntegrationCard
            key={integration.id}
            integration={integration}
            organizationId={organization?.id || ''}
            organizationSlug={organization?.slug || orgId}
          />
        ))}
      </div>

      <Card className="border-dashed border-2 bg-gray-50">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <MessageSquareIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold mb-2">Need another integration?</h3>
              <p className="text-sm text-gray-600 mb-4">
                We're always looking to add more integrations. If you need a specific service that's not listed here, 
                let us know and we'll consider adding it to our roadmap.
              </p>
              <Button
                variant="default"
                onClick={() => {
                  const subject = encodeURIComponent('Integration Request');
                  const body = encodeURIComponent(`Hi Envase team,\n\nI'd like to request the following integration:\n\nService: [Service name]\nUse case: [Describe how you'd use it]\n\nThank you!`);
                  window.open(`mailto:support@envase.com?subject=${subject}&body=${body}`, '_blank');
                }}
              >
                Request Integration
                <ExternalLinkIcon className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

